<template>
  <v-container>
    <Guide />

    <div id="page-heading" class="grey--text text-h5 text-sm-h4 text-md-h3 text-l-h2 text-xl-h2 mt-2">
      Schedule
    </div>

    <v-row flex>
      <v-col cols="12">
        <v-data-table
        :headers="headers"
        :items="teachingSessions"
        :custom-sort="customSort"
        must-sort
        sort-by="startEndTime"
        sort-desc
        :footer-props="{
          itemsPerPageOptions: [10, 20]
        }"
        >
          <template v-slot:top>
            <v-toolbar flat color="white">
              <v-spacer></v-spacer>

              <v-dialog v-model="dialogVisible" max-width="600px" @click:outside="resetFormFields()">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    color="primary"
                    depressed
                  >
                    <v-icon left>add</v-icon><span>Session</span>
                  </v-btn>
                </template>
                <v-card outlined>
                  <v-form ref="form">
                    <v-card-title>
                      Add a teaching session
                    </v-card-title>

                    <v-card-text>
                      <v-container>
                        <v-row>
                          <v-col cols="6">
                            <v-select
                              v-model="subject"
                              label="Subject"
                              :items="subjects"
                              return-object
                              item-text="data.title"
                              :rules="[inputRules.required]"
                              @change="tutorialPath = null"
                            ></v-select>
                          </v-col>
                          <v-col cols="6">
                            <v-select
                              v-model="tutorial"
                              label="Tutorial"
                              :items="tutorials"
                              return-object
                              item-text="data.title"
                              :rules="[inputRules.required]"
                            ></v-select>
                          </v-col>
                        </v-row>

                        <v-row>
                          <v-col cols="6">
                            <v-datetime-picker 
                              v-model="startTime"
                              label="Start time"  
                              :date-format="dateFormat"
                              :text-field-props="{ prependIcon: 'event' }"
                              :date-picker-props="{ noTitle: true }"
                              :time-picker-props="{ format: '24hr', noTitle: true }"
                            ></v-datetime-picker>
                          </v-col>
                          <v-col cols="6">
                            <v-select
                              v-model="durationMinutes"
                              label="Duration"
                              :items="durations"
                              item-text="text"
                              item-value="mins"
                              :rules="[inputRules.required]"
                            ></v-select>
                          </v-col>
                        </v-row>

                        <v-row>
                          <v-col cols="12">
                            <div class="mt-4 mb-2">
                              Go to <a href="https://meet.google.com" target="_BLANK">meet.google.com <v-icon small>open_in_new</v-icon></a> to create a meeting link.
                            </div>
                            <div class="mb-4">
                              Click the "New Meeting" button and choose "Get a meeting link to share"
                            </div>
                            <v-text-field
                              v-model="meetingLink"
                              label="Google Meet Link"
                              :rules="[inputRules.required, inputRules.isGoogleMeetUrl]">
                            </v-text-field>
                          </v-col>
                        </v-row>
                      </v-container>
                    </v-card-text>

                    <v-card-actions>
                      <v-spacer />
                      <v-btn depressed @click="add" :loading="submitting">Add</v-btn>
                    </v-card-actions>
                  </v-form>
                </v-card>
              </v-dialog>
            </v-toolbar>


            <v-dialog v-if="editItem" v-model="editDialogVisible" max-width="600px" @click:outside="resetFormFields()">
              <v-card outlined>
                <v-form ref="editForm">
                  <v-card-title>
                    Edit teaching session
                  </v-card-title>

                  <v-card-text>
                    
                    <v-container>
                      <v-row>
                        <v-col cols="6">
                          <v-text-field
                            v-model="editItem.tutorial.subject.data.title"
                            label="Subject"
                            readonly
                          ></v-text-field>
                        </v-col>
                        <v-col cols="6">
                          <v-text-field
                            v-model="editItem.tutorial.data.title"
                            label="Tutorial"
                            readonly
                          ></v-text-field>
                        </v-col>
                      </v-row>

                      <v-row>
                        <v-col cols="6">
                          <v-datetime-picker 
                            v-model="startTime" 
                            label="Start time" 
                            :date-format="dateFormat"
                            :text-field-props="{ prependIcon: 'event' }"
                            :date-picker-props="{ noTitle: true }"
                            :time-picker-props="{ format: '24hr', noTitle: true }"
                          ></v-datetime-picker>
                        </v-col>
                        <v-col cols="6">
                          <v-select
                            v-model="durationMinutes"
                            label="Duration"
                            :items="durations"
                            item-text="text"
                            item-value="mins"
                            :rules="[inputRules.required]"
                          ></v-select>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="12">
                          <div class="mt-4 mb-2">
                            Go to <a href="https://meet.google.com" target="_BLANK">meet.google.com <v-icon small>open_in_new</v-icon></a> to create a meeting link.
                          </div>
                          <div class="mb-4">
                            Click the "New Meeting" button and choose "Get a meeting link to share"
                          </div>
                          <v-text-field
                            v-model="meetingLink"
                            label="Google Meet Link"
                            :rules="[inputRules.required, inputRules.isGoogleMeetUrl]">
                          </v-text-field>
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-card-text>

                  <v-card-actions>
                    <v-spacer />
                    <v-btn depressed @click="update" :loading="submitting">Update</v-btn>
                  </v-card-actions>
                </v-form>
              </v-card>
            </v-dialog>


            <v-dialog v-if="shareItem" v-model="shareDialogVisible" max-width="600px">
              <v-card outlined>
                <v-form ref="shareForm">
                  <v-card-title>
                    Share
                  </v-card-title>
                  <v-card-subtitle>
                    Invite students to this tutorial by sharing a link with them
                  </v-card-subtitle>

                  <v-card-text>
                    <v-text-field
                      class="text-body-2"
                      :value="shareUrl"
                      label="Invite link"
                      readonly
                    >
                      <v-icon slot="append" @click="copyLinkToClipboard">content_copy</v-icon>
                    </v-text-field>
                  </v-card-text>
                </v-form>
              </v-card>
            </v-dialog>

          </template>

          <template v-slot:item.startEndTime="{ item }">
            {{ item.data.startTime | date('EEE do MMM yyyy, HH:mm') }} - {{ item.data.endTime | date('HH:mm') }}
          </template>

          <template v-slot:item.actions="{ item }">

            <v-icon
              class="mr-3"
              @click="showShareDialog(item)"
            >
              share
            </v-icon>

            <v-icon
              class="mr-3"
              @click="showEditDialog(item)"
            >
              edit
            </v-icon>

            <v-icon
              @click="archiveItem(item)"
            >
              delete
            </v-icon>
          </template>

        </v-data-table>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { v4 as uuidv4 } from 'uuid'
import addMinutes from 'date-fns/addMinutes'
import { mapGetters } from 'vuex'
import Guide from '@/components/lecturer/Guide'
import firebase from '@/firebase.config'

export default {
  name: "Schedule",
  components: { Guide },
  data: function () {
    return {
      dialogVisible: false,
      submitting: false,

      editDialogVisible: false,
      editItem: null,

      shareDialogVisible: false,
      shareItem: null,

      subject: null,
      tutorial: null,
      dateFormat: "EEE do MMM yyyy,",
      startTime: null,
      durationMinutes: null,
      meetingLink: null,
      headers: [
        { text: 'Time', value: 'startEndTime'},
        { text: 'Subject', value: 'tutorial.subject.data.title', sortable: false},
        { text: 'Tutorial', value: 'tutorial.data.title', sortable: false},
        { text: 'Actions', value: 'actions', sortable: false, align: 'end'}
      ],
      inputRules: {
        required: value => !!value || 'Required',
        isGoogleMeetUrl: value => (!!value && value.startsWith('https://meet.google.com/')) || 'Google Meet links start with https://meet.google.com/'
      },
      durations: [
        {mins: 30, text: '0.5 hours'},
        {mins: 60, text: '1 hour'},
        {mins: 90, text: '1.5 hours'},
        {mins: 120, text: '2 hours'},
        {mins: 180, text: '3 hours'},
        {mins: 240, text: '4 hours'},
        {mins: 300, text: '5 hours'},
        {mins: 360, text: '6 hours'},
        {mins: 420, text: '7 hours'},
        {mins: 480, text: '8 hours'},
      ]
    }
  },
  methods: {
    customSort(items, index, isDesc) {
      // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/sort
      // If compareFunction(a, b) returns less than 0, 
      // sort a to an index lower than b (i.e. a comes first).
      if (isDesc[0]) {
        items.sort((a, b) => b.data.startTime - a.data.startTime)
      }
      else {
        items.sort((a, b) => a.data.startTime - b.data.startTime)
      }
      return items
    },
    async add() {
      if (this.$refs.form.validate()) {
        this.submitting = true

        // Create the teachingSession
        const teachingSessionData = {
          startTime: this.startTime,
          endTime: this.endTime,
          durationMinutes: this.durationMinutes,
          meetingLink: this.meetingLink,
          inviteCode: uuidv4(),
          isArchived: false,
          parentPath: this.tutorial.path
        }
        await this.$store.dispatch('addTeachingSession', teachingSessionData)

        // If this is the first time creating a teachingSession update their completeSteps
        if (!this.lecturer.data.completedSteps.includes('SCHEDULE_TEACHING_SESSION')) {
          this.lecturer.data.completedSteps.push('SCHEDULE_TEACHING_SESSION')
          await this.$store.dispatch('updateLecturer', this.lecturer)
        }

        // Report to GA
        firebase.analytics().logEvent('schedule_tutorial')

        this.subject = null
        this.tutorial = null
        this.resetFormFields()

        this.submitting = false
        this.dialogVisible = false
      }
    },
    showEditDialog(item) {
      this.editItem = item

      // Copy from doc to local variables so
      // changes can be reverted
      this.startTime = item.data.startTime
      this.durationMinutes = item.data.durationMinutes
      this.meetingLink = item.data.meetingLink

      this.editDialogVisible = true
    },
    async update() {
      if (this.$refs.editForm.validate()) {
        this.submitting = true

        // Copy changes from local variables to the object
        this.editItem.data.startTime = this.startTime
        this.editItem.data.endTime = this.endTime
        this.editItem.data.durationMinutes = this.durationMinutes
        this.editItem.data.meetingLink = this.meetingLink
        // Update Firestore
        await this.$store.dispatch('updateTeachingSession', this.editItem)

        this.resetFormFields()

        this.submitting = false
        this.editDialogVisible = false
      }
    },
    async archiveItem(item) {
      const res = await this.$confirm('Are you sure you want to archive this teaching session?')
      if (res === true) {
        item.data.isArchived = true
        await this.$store.dispatch('updateTeachingSession', item)
      }
    },
    showShareDialog(item) {
      this.shareItem = item
      this.shareDialogVisible = true
    },
    async copyLinkToClipboard() {
      await navigator.clipboard.writeText(this.shareUrl)
      this.$store.dispatch('storeSnackbarMessage', 'Copied to clipboard')

      // If this is the first time copying an invite link then update their completeSteps
      if (!this.lecturer.data.completedSteps.includes('COPY_INVITE_LINK')) {
        this.lecturer.data.completedSteps.push('COPY_INVITE_LINK')
        await this.$store.dispatch('updateLecturer', this.lecturer)
      }

      // Report to GA
      firebase.analytics().logEvent('share_tutorial')
    },
    resetFormFields() {
      // Default to the start of the current hour
      this.subject = null
      this.startTime = new Date(new Date().setMinutes(0,0,0))
      this.durationMinutes = 60
      this.meetingLink = null
    }
  },
  computed: {
    ...mapGetters({
      lecturer: "lecturer"
    }),
    subjects() {
      let subjects = []
      if (this.lecturer.subjects) {
        subjects = this.lecturer.subjects.filter(sub => sub.data.isArchived === false)
      }
      return subjects
    },
    tutorials() {
      if (this.subject && this.subject.tutorials) {
        return this.subject.tutorials.filter(tut => tut.data.isArchived == false)
      }
      else {
        return []
      }
    },
    teachingSessions() {
      let teachingSessions = []
      
      this.lecturer.subjects && this.lecturer.subjects.forEach(subject => {
        subject.tutorials && subject.tutorials.forEach(tutorial => {
          tutorial.teachingSessions && tutorial.teachingSessions.forEach(teachingSession => {
            // Ignore archived teachingSessions
            if (teachingSession.data.isArchived == false) {
              teachingSessions.push(teachingSession)
            }
          })
        })
      })
      return teachingSessions
    },
    endTime() {
      let endTime = null
      if (this.startTime && this.durationMinutes) {
        endTime = addMinutes(this.startTime, this.durationMinutes)
      }
      return endTime
    },
    shareUrl() {
      let shareUrl = null
      if (this.shareItem) {
        shareUrl = document.location.origin + '/student/invite/' + this.shareItem.data.inviteCode
      }
      return shareUrl
    }
  },
  created() {
    this.resetFormFields()
  },
  mounted() {
    // Scroll to below the guide
    this.$vuetify.goTo('#page-heading', {
      duration: 1000,
      easing: 'easeInOutCubic'
    })
  }
}
</script>

<style>
</style>
